import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { isStandalone, isIOS, isAndroid } from "@/lib/installPrompt";
import { viaSharePath, clearViaShare, isShareablePath, openInNativeApp, autoOpenInNativeApp, PLAY_STORE_URL } from "@/lib/shareLinks";
import { isBot } from "@/lib/bots";

const DISMISS_KEY = "yalola.openInAppDismissed";
const inNativeInAppBrowser = () => { try { return sessionStorage.getItem("yalola.inAppBrowser") === "1"; } catch { return false; } };
const dismissed = () => { try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; } };

// Mobile web only: a member who arrives through a shared link (WhatsApp, Facebook, SMS…) can
// hand the page over to the installed app with ONE tap. A browser cannot know whether the app
// is installed (privacy) — a user gesture is the only reliable way to open a custom scheme
// from in-app browsers, so the attempt is made on tap; when nothing opens we point to the store.
export const OpenInAppBanner = () => {
  const { t, config } = useApp();
  const { pathname, search } = useLocation();
  const [path, setPath] = useState(null);
  const [notInstalled, setNotInstalled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [overlay, setOverlay] = useState(false);

  // Vidéo / reel ouvert en visionneuse plein écran (z-100) : le bandeau passe AU-DESSUS, en haut (sous la croix de
  // fermeture) pour ne pas couvrir la légende ni les réactions en bas.
  useEffect(() => {
    if (!path) return undefined;
    const check = () => setOverlay(!!document.querySelector('[data-testid="lightbox"]'));
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true });
    return () => mo.disconnect();
  }, [path]);

  useEffect(() => {
    if (isStandalone() || inNativeInAppBrowser() || dismissed() || isBot()) return;
    if (!isAndroid() && !isIOS()) return;
    const via = viaSharePath();
    const current = pathname + search;
    if (via) setPath(via);   // la page de partage /api/e/… a déjà tenté l'ouverture : pas de 2e relance automatique
    else if (isShareablePath(current) && !pathname.startsWith("/s/")) {
      setPath(current);
      autoOpenInNativeApp(current);   // lien direct yalola.app/post/… collé dans Chrome : l'app s'ouvre toute seule si installée
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!path || config?.settings?.pwa?.install_enabled === false) return null;
  const pwa = config?.settings?.pwa || {};
  const storeUrl = isIOS() ? (pwa.store_ios_url || "") : (pwa.store_android_url || PLAY_STORE_URL);

  const close = () => { try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ } clearViaShare(); setPath(null); };
  const open = async () => {
    setBusy(true);
    // Follow the member's navigation since landing: open the page they are looking at now.
    const target = isShareablePath(pathname + search) ? pathname + search : path;
    const opened = await openInNativeApp(target, { storeUrl });
    setBusy(false);
    if (opened) { close(); return; }
    setNotInstalled(true);
    toast.info(t("share.not_installed", "L'app Yalola ne semble pas installée sur cet appareil."));
  };

  return (
    <div className="lg:hidden fixed left-0 right-0 z-[110] px-3 pointer-events-none"
         style={overlay ? { top: "calc(var(--sa-top, 0px) + 4.5rem)" } : { bottom: "calc(3.5rem + var(--sa-bottom, 0px) + 0.5rem)" }}
         data-testid="open-in-app-banner" data-path={path} data-position={overlay ? "top" : "bottom"}>
      <div className="pointer-events-auto mx-auto max-w-md rounded-2xl shadow-lg border px-3 py-2.5 flex items-center gap-3"
           style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }}>
        <img src="/logo192.png" alt="" className="h-10 w-10 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate" data-testid="open-in-app-title">{t("share.open_in_app", "Ouvrir dans l'app Yalola")}</div>
          {notInstalled && storeUrl ? (
            <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-medium underline" style={{ color: "var(--yalola-primary)" }} data-testid="open-in-app-store">
              {t("share.get_app", "Pas encore installée ? Télécharger")}
            </a>
          ) : (
            <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{t("share.open_in_app_hint", "Plus fluide, avec les notifications")}</div>
          )}
        </div>
        <Button size="sm" className="pill-primary rounded-full shrink-0" onClick={open} disabled={busy} data-testid="open-in-app-open">
          <ExternalLink size={14} className="mr-1" /> {t("share.open", "Ouvrir")}
        </Button>
        <button type="button" onClick={close} aria-label={t("common.close", "Fermer")} className="shrink-0 p-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="open-in-app-dismiss"><X size={16} /></button>
      </div>
    </div>
  );
};

export default OpenInAppBanner;
